import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  alertKind,
  categorize,
  coverageLabel,
  etaToPoint,
  type AreaGeometry,
  type StormMotion,
} from '@ww/shared';
import WeatherMap from '../components/WeatherMap';
import LightningCard from '../components/LightningCard';
import WhenHitsCard from '../components/WhenHitsCard';
import MapTools, { zoneAsAssetDraft } from '../components/MapTools';
import SidebarPanel from '../components/SidebarPanel';
import { useMapDraw } from '../hooks/useMapDraw';
import { api } from '../api';
import { useNow } from '../lib/time';
import type { DrawnZone } from '../lib/mapTools';
import type { ImpactedPlace } from '../components/MapTools';
import { getRadarPrefs, setRadarPrefs } from '../lib/radarPrefs';
import { useNowcast } from '../hooks/useNowcast';
import { usePlaces } from '../hooks/usePlaces';
import { ActiveBanner, AlertFeed, StaleBanner, StatusTiles } from '../components/DashboardParts';
import { useLive } from '../hooks/useLive';
import HeaderActions from '../components/HeaderActions';
import { I, Icon } from '../lib/icons';

type LayerKey = 'radar' | 'warnings' | 'watches' | 'assets' | 'closures' | 'lightning';
// Icon-only in the header, so the label survives only as the tooltip and the
// accessible name — both carry the full phrase, never an abbreviation. "Warnings"
// versus "Watches" is a distinction that matters to the people using this.
const LAYERS: [LayerKey, string, string][] = [
  ['radar', 'Radar', I.target],
  ['warnings', 'Warnings', I.alert],
  ['watches', 'Watches & advisories', I.watch],
  ['assets', 'Assets', I.pin],
  ['closures', 'Road closures', I.cone],
  ['lightning', 'Lightning', I.bolt],
];

export default function Dashboard() {
  const { dashboard, health, assets, closures, lightning, tenant, lastError } = useLive();
  const navigate = useNavigate();
  const [layers, setLayers] = useState<Record<LayerKey, boolean>>({
    radar: true,
    warnings: true,
    watches: true,
    assets: true,
    closures: true,
    lightning: true,
  });
  const allAlerts = dashboard?.alerts ?? [];
  const mapAlerts = useMemo(
    () =>
      allAlerts.filter((ta) => {
        const cat = ta.evaluation?.category ?? categorize(ta.alert.event);
        return cat === 'warning' ? layers.warnings : layers.watches;
      }),
    [allAlerts, layers.warnings, layers.watches],
  );
  // One clock for the tools, so estimates tick down instead of freezing at the
  // moment the shape was drawn. 15 s is well under the 5-minute ETA bucket.
  const nowMs = useNow(15_000);
  /*
   * The radar nowcast covers a box about 45 miles around its anchor. Anchoring it on
   * the county centre means an arrival pin dropped outside that box has no rain to
   * measure against and silently reports nothing. Follow the pin instead, falling back
   * to the county. The pin lives inside useMapDraw, which also consumes the nowcast, so
   * the anchor is carried in state and updated after the fact rather than read directly
   * — one render behind, which costs nothing against a request that is already async.
   */
  const [rainAnchor, setRainAnchor] = useState<[number, number] | null>(null);
  const [swathMinutes, setSwathMinutesState] = useState(() => getRadarPrefs(tenant?._id).swathMinutes);
  const setSwathMinutes = useCallback(
    (m: number) => setSwathMinutesState(setRadarPrefs(tenant?._id, { swathMinutes: m }).swathMinutes),
    [tenant?._id],
  );
  const rain = useNowcast(rainAnchor ?? tenant?.center ?? null);
  const places = usePlaces(tenant?.center ?? null);
  const draw = useMapDraw(tenant?._id, allAlerts, nowMs, rain, swathMinutes);
  const pin = draw.state.pin;
  /*
   * Follow whichever the user last pointed at. The nowcast covers ~45 miles around its
   * anchor, so a storm-path tap outside that box would otherwise be answered with the
   * county's rain — or, if the county is clear, with nothing at all, which is
   * indistinguishable from the tool being broken.
   */
  const pathPoint = draw.state.pathPick?.point ?? null;
  useEffect(() => {
    setRainAnchor(pathPoint ?? pin ?? null);
  }, [pathPoint?.[0], pathPoint?.[1], pin?.[0], pin?.[1]]);
  const [savingZoneId, setSavingZoneId] = useState<string | null>(null);
  const [hitsOpen, setHitsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [ltgOpen, setLtgOpen] = useState(false);

  /**
   * Promote a drawn radius to a real Asset. Drawn zones are browser-local by design;
   * this is the one path that hands a zone to the server, and it reuses the existing
   * asset pipeline rather than inventing a parallel one.
   */
  const saveZone = useCallback(
    async (zone: DrawnZone) => {
      const name = `Watch zone ${new Date().toLocaleDateString()}`;
      setSavingZoneId(zone.id);
      try {
        await api.createAsset(zoneAsAssetDraft(zone, name));
        draw.removeZone(zone.id);
      } catch {
        /* the zone stays drawn; the assets page reports failures properly */
      } finally {
        setSavingZoneId(null);
      }
    },
    [draw],
  );


  /*
   * What the projected paths would cross. Uses `etaToPoint` rather than a point-in-swath
   * test: the swath is a drawing, but etaToPoint ray-casts along the actual track, so it
   * excludes places the storm passes beside and gives each one a real arrival time.
   */
  const impacted = useMemo<ImpactedPlace[]>(() => {
    const now = new Date().toISOString();
    // Every warning that could actually be projected, plus a tapped storm if there is one.
    const storms: Array<{ geometry: AreaGeometry; motion: StormMotion }> = [];
    for (const ta of allAlerts) {
      const cat = ta.evaluation?.category ?? categorize(ta.alert.event);
      if (cat !== 'warning' || !ta.alert.geometry || !ta.alert.motion) continue;
      storms.push({ geometry: ta.alert.geometry, motion: ta.alert.motion });
    }
    if (draw.pickedPath?.geometry && draw.pickedPath.motion) {
      storms.push({ geometry: draw.pickedPath.geometry, motion: draw.pickedPath.motion });
    }
    if (!storms.length) return [];

    const soonest = (point: [number, number], key: string, name: string, kind: 'asset' | 'town') => {
      let best: ImpactedPlace | null = null;
      for (const st of storms) {
        const eta = etaToPoint(st.geometry, st.motion, point, now);
        if (eta.status !== 'arriving' && eta.status !== 'inside') continue;
        if ((eta.minutesLow ?? 0) > swathMinutes) continue;
        if (!best || (eta.minutesLow ?? 0) < (best.eta.minutesLow ?? 0)) best = { key, name, kind, eta };
      }
      return best;
    };

    const rank = (p: ImpactedPlace) => (p.eta.status === 'inside' ? -1 : p.eta.minutesLow ?? 0);
    const sites: ImpactedPlace[] = [];
    for (const a of assets ?? []) {
      if (!a.enabled) continue;
      const hit = soonest(a.location.coordinates, a._id, a.name, 'asset');
      if (hit) sites.push(hit);
    }
    const towns: ImpactedPlace[] = [];
    for (const pl of places) {
      const hit = soonest(pl.center, `t:${pl.geoid}`, pl.name, 'town');
      if (hit) towns.push(hit);
    }
    // Sites first, always: those are the ones you are responsible for.
    return [
      ...sites.sort((x, y) => rank(x) - rank(y)),
      ...towns.sort((x, y) => rank(x) - rank(y)).slice(0, 10),
    ];
  }, [assets, allAlerts, places, swathMinutes, draw.pickedPath]);

  const radarStatus = health?.components?.radar?.status;
  const radarDegraded = !!health && radarStatus !== 'ok';
  const hasTornado = allAlerts.some((a) => alertKind(a.alert.event) === 'tor');

  return (
    <div className="page" style={{ gap: 14 }}>
      {lastError && !dashboard && <div className="err-box">Could not load dashboard: {lastError}</div>}
      <ActiveBanner dashboard={dashboard} />
      <StaleBanner health={health} tz={tenant?.timezone} />

      <div className="dash-grid">
        <section className="panel" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 12px 10px 16px',
              borderBottom: '1px solid #1e2c46',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1, minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Radar and warnings</h2>
              <div style={{ fontSize: 12.5, color: '#93a5c1' }}>
                NWS alerts · NOAA radar{hasTornado ? ' · Tornado warning outlines pulse' : ''}
              </div>
            </div>
            {radarDegraded && (
              <span
                className="tag"
                style={{ background: '#4a3410', color: '#ffd08a', fontSize: 12, padding: '4px 8px' }}
                title={health?.components?.radar?.detail || undefined}
              >
                RADAR {String(radarStatus ?? 'unknown').toUpperCase()}
              </span>
            )}
          </div>
          {/*
            These live in the app header, not this panel: four layer chips wrapping to a
            second line took this row from ~57px to ~100px on a narrower window, and that
            came straight out of the map.
          */}
          <HeaderActions>
            <div role="group" aria-label="Map tools" style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm hov"
                aria-pressed={hitsOpen}
                onClick={() => setHitsOpen((v) => !v)}
              >
                When will it hit?
              </button>
              <button
                type="button"
                className="chip hov"
                aria-pressed={toolsOpen}
                title="Measure, drop an arrival pin, draw a watch zone, or mark up the map"
                onClick={() => {
                  setToolsOpen((v) => {
                    if (v) draw.setMode('none');
                    return !v;
                  });
                }}
              >
                Tools
              </button>
              {/* Only offered when the server is actually feeding us flashes: a control
                  for a layer that is switched off upstream is a control that lies. */}
              {!!lightning?.length && (
                <button
                  type="button"
                  className="chip hov"
                  aria-pressed={ltgOpen}
                  title="How close the lightning is to each asset"
                  onClick={() => setLtgOpen((v) => !v)}
                >
                  Lightning
                </button>
              )}
            </div>
            <div role="group" aria-label="Map layers" style={{ display: 'flex', gap: 6 }}>
              {LAYERS.map(([key, label, icon]) => (
                <button
                  key={key}
                  type="button"
                  className="chip icon-chip hov"
                  aria-pressed={layers[key]}
                  // Both, deliberately: with no visible text the tooltip is all a sighted
                  // user has and the label is all a screen reader has.
                  aria-label={label}
                  title={label}
                  onClick={() => setLayers((l) => ({ ...l, [key]: !l[key] }))}
                >
                  <Icon d={icon} size={17} />
                </button>
              ))}
            </div>
          </HeaderActions>
          <div style={{ position: 'relative', flexGrow: 1, minHeight: 320 }}>
            <WeatherMap
              tenant={tenant}
              alerts={mapAlerts}
              assets={layers.assets ? assets ?? [] : []}
              closures={layers.closures ? closures ?? [] : []}
              lightning={layers.lightning ? lightning ?? [] : []}
              // Tapping a closure opens it on its own page, the same way tapping a
              // warning polygon opens the alert.
              onClosureClick={(id) => navigate(`/closures?sel=${encodeURIComponent(id)}`)}
              showRadar={layers.radar}
              showSwaths
              tools={draw.toolsProp}
            />
          </div>
          {/*
            The panels live in the sidebar, not over the map: floating them covered the
            legend and the radar scrubber. The map now keeps every pixel it has.
          */}
          <SidebarPanel>
            {hitsOpen && (
              <WhenHitsCard
                onClose={() => setHitsOpen(false)}
                tenantId={tenant?._id}
                tenantCenter={tenant?.center}
                tenantLabel={coverageLabel(tenant)}
                timezone={tenant?.timezone}
                alerts={allAlerts}
                assets={assets ?? []}
              />
            )}
            {ltgOpen && (
              <LightningCard
                onClose={() => setLtgOpen(false)}
                flashes={lightning ?? []}
                assets={assets ?? []}
              />
            )}
            {toolsOpen && (
              <MapTools
                onClose={() => setToolsOpen(false)}
                draw={draw}
                impacted={impacted}
                swathMinutes={swathMinutes}
                onSwathMinutes={setSwathMinutes}
                onSaveZone={saveZone}
                savingZoneId={savingZoneId}
              />
            )}
          </SidebarPanel>
        </section>

        <AlertFeed dashboard={dashboard} health={health} />
      </div>

      <StatusTiles dashboard={dashboard} />
    </div>
  );
}
